<template>
  <div class="print-page" v-if="q">
    <!-- 인쇄 버튼 (인쇄 시 숨김) -->
    <div class="no-print action-bar">
      <el-button type="primary" @click="doPrint">인쇄</el-button>
      <el-button @click="goBack">돌아가기</el-button>
    </div>

    <!-- 견적서 본문 -->
    <div class="quotation-sheet">
      <!-- 상단 헤더 -->
      <div class="sheet-header">
        <div class="company-logo">
          <img v-if="company.logo_url" :src="company.logo_url" alt="logo" class="logo-img" />
          <span v-else>{{ company.name_en || company.name }}</span>
        </div>
        <h1 class="sheet-title">{{ q.quotation_type === 'PC' ? '사 전 원 가 계 산 서' : '견 적 서' }}</h1>
        <div class="sheet-date">{{ formatDate(q.request_date) }}</div>
      </div>

      <!-- 기본정보 테이블 -->
      <table class="info-tbl">
        <tbody>
          <tr>
            <th>브랜드</th>
            <td>{{ q.brand_name }}</td>
            <th>납기일</th>
            <td>{{ formatDate(q.delivery_date) }}</td>
          </tr>
          <tr>
            <th>STYLE</th>
            <td>{{ q.design_style_number }}</td>
            <th>발주수량</th>
            <td>{{ q.order_quantity?.toLocaleString() }} PCS</td>
          </tr>
          <tr>
            <th>디자이너</th>
            <td>{{ q.designer_name || '-' }}</td>
            <th>생산국</th>
            <td>{{ q.cmt_detail?.country || '-' }}</td>
          </tr>
          <tr>
            <th>담당자</th>
            <td>{{ author.name }}{{ author.position ? ' (' + author.position + ')' : '' }}</td>
            <th>연락처</th>
            <td>{{ author.phone || '-' }}{{ author.email ? ' / ' + author.email : '' }}</td>
          </tr>
        </tbody>
      </table>

      <template v-if="canViewCosts">
        <!-- 원부자재 통합 테이블 -->
        <div class="section-title">원 부 자 재</div>
        <table class="mat-tbl">
          <thead>
            <tr>
              <th class="col-type">구분</th>
              <th class="col-name">품 명</th>
              <th class="col-spec">규 격</th>
              <th class="col-price">단 가</th>
              <th class="col-qty">소요량</th>
              <th class="col-amt">금 액</th>
              <th class="col-cmt">비고</th>
            </tr>
          </thead>
          <tbody>
            <!-- 원자재 -->
            <tr v-for="(m, idx) in rawMaterials" :key="'raw-' + m.id">
              <td v-if="idx === 0" :rowspan="rawMaterials.length" class="center type-cell">원자재</td>
              <td>{{ m.item_name }}</td>
              <td>{{ m.specification }}</td>
              <td class="right">{{ fmtNum(m.unit_price_krw) }}</td>
              <td class="right">{{ m.usage_per_unit }}</td>
              <td class="right">{{ fmtNum(m.amount) }}</td>
              <td class="center">{{ m.is_cmt ? 'CMT' : '' }}</td>
            </tr>
            <tr class="subtotal-row">
              <td colspan="5" class="right"><strong>원 자 재 계</strong></td>
              <td class="right"><strong>{{ fmtNum(rawMaterialTotal) }}</strong></td>
              <td></td>
            </tr>
            <!-- 부자재 -->
            <tr v-for="(m, idx) in accMaterials" :key="'acc-' + m.id">
              <td v-if="idx === 0" :rowspan="accMaterials.length" class="center type-cell">부자재</td>
              <td>{{ m.item_name }}</td>
              <td>{{ m.specification }}</td>
              <td class="right">{{ fmtNum(m.unit_price_krw) }}</td>
              <td class="right">{{ m.usage_per_unit }}</td>
              <td class="right">{{ fmtNum(m.amount) }}</td>
              <td class="center">{{ m.is_cmt ? 'CMT' : '' }}</td>
            </tr>
            <tr class="subtotal-row">
              <td colspan="5" class="right"><strong>부 자 재 계</strong></td>
              <td class="right"><strong>{{ fmtNum(accMaterialTotal) }}</strong></td>
              <td></td>
            </tr>
          </tbody>
        </table>

        <!-- 원가 요약 + 디자인 이미지 -->
        <div class="section-title">원 가 요 약</div>
        <div class="cost-image-row">
          <table class="cost-tbl">
            <tbody>
              <tr>
                <th>원자재계</th>
                <td class="right" colspan="2">{{ fmtNum(rawMaterialTotal) }}</td>
              </tr>
              <tr>
                <th>부자재계</th>
                <td class="right" colspan="2">{{ fmtNum(accMaterialTotal) }}</td>
              </tr>
              <tr>
                <th>임가공임</th>
                <td class="right" colspan="2">{{ fmtNum(q.cmt_cost) }}</td>
              </tr>
              <tr>
                <th>경비</th>
                <td class="right">물류 {{ fmtNum(q.logistics_cost) }}</td>
                <td class="right">관세 {{ fmtNum(q.customs_cost) }}</td>
              </tr>
              <tr class="highlight-row">
                <th>제 조 원 가</th>
                <td class="right bold" colspan="2">{{ fmtNum(q.manufacturing_cost) }}</td>
              </tr>
              <tr>
                <th>마진 {{ q.margin_rate }}%</th>
                <td class="right" colspan="2">{{ fmtNum(q.margin_amount) }}</td>
              </tr>
              <tr class="total-row">
                <th>견 적 가</th>
                <td class="right bold" colspan="2">{{ fmtNum(q.quotation_price) }}</td>
              </tr>
              <tr v-if="q.fix_unit_price">
                <th>FIX 단가</th>
                <td class="right bold" colspan="2">{{ fmtNum(q.fix_unit_price) }}</td>
              </tr>
              <tr v-if="q.negotiated_price" class="nego-row">
                <th>합의가(NEGO)</th>
                <td class="right bold" colspan="2">{{ fmtNum(q.negotiated_price) }}</td>
              </tr>
            </tbody>
          </table>
          <div class="design-image-area" v-if="q.design_image_url">
            <div class="image-label">{{ q.quotation_type === 'QT' ? 'Sample Sketch' : 'Design Image' }}</div>
            <img :src="q.design_image_url" :alt="q.quotation_type === 'QT' ? '샘플 스케치' : '디자인 이미지'" class="design-img" />
          </div>
        </div>

        <!-- 사이즈 배분 (있는 경우) -->
        <template v-if="q.size_distributions?.length">
          <div class="section-title">사이즈 배분</div>
          <table class="size-tbl">
            <thead>
              <tr>
                <th v-for="sd in q.size_distributions" :key="sd.id">{{ sd.size }}</th>
                <th>합계</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td v-for="sd in q.size_distributions" :key="sd.id" class="center">{{ sd.quantity?.toLocaleString() }}</td>
                <td class="center bold">{{ q.order_quantity?.toLocaleString() }}</td>
              </tr>
            </tbody>
          </table>
        </template>
      </template>

      <!-- 메모 -->
      <div v-if="q.memo" class="memo-box">
        <div class="memo-label">비고</div>
        <div class="memo-text">{{ q.memo }}</div>
      </div>

      <!-- 하단 서명 영역 -->
      <div class="sheet-footer">
        <div class="footer-left">
          <p class="footer-company-name">{{ company.name_en || company.name }}</p>
          <p v-if="company.address" class="footer-line">{{ company.address }}</p>
          <p class="footer-line">
            <span v-if="company.phone">TEL: {{ company.phone }}</span>
            <span v-if="company.fax"> / FAX: {{ company.fax }}</span>
            <span v-if="company.email"> / {{ company.email }}</span>
          </p>
        </div>
        <div class="footer-right">
          <span class="author-info">{{ author.name }}{{ author.department ? ' / ' + author.department : '' }}</span>
          <span v-if="author.phone" class="author-info">{{ author.phone }}</span>
          <div class="seal-area">(인)</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axiosInstance from '@/api/axios'
import { useUserStore } from '@/stores/user'

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const q = ref(null)

const canViewCosts = computed(() => userStore.hasQuotationAccess || userStore.hasCostAccess)

const company = computed(() => {
  if (q.value?.company_info) return q.value.company_info
  return { name: '', name_en: '' }
})

const author = computed(() => {
  if (q.value?.company_info?.author) return q.value.company_info.author
  return { name: '-', department: '', position: '', phone: '', email: '' }
})

const rawMaterials = computed(() => (q.value?.materials || []).filter(m => m.material_type === 'RAW'))
const accMaterials = computed(() => (q.value?.materials || []).filter(m => m.material_type === 'ACC'))

const rawMaterialTotal = computed(() =>
  rawMaterials.value.reduce((sum, m) => sum + Number(m.amount || 0), 0)
)
const accMaterialTotal = computed(() =>
  accMaterials.value.reduce((sum, m) => sum + Number(m.amount || 0), 0)
)

const fmtNum = (val) => {
  if (!val && val !== 0) return '-'
  return Number(val).toLocaleString()
}

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return `${d.getFullYear()}년 ${String(d.getMonth() + 1).padStart(2, '0')}월 ${String(d.getDate()).padStart(2, '0')}일`
}

const doPrint = () => {
  window.print()
}

const goBack = () => {
  if (window.history.length > 1) {
    router.back()
  } else {
    window.close()
  }
}

onMounted(async () => {
  try {
    const response = await axiosInstance.get(`v2/estimate/quotations/${route.params.id}/`)
    q.value = response.data
  } catch (error) {
    console.error('Failed to fetch quotation:', error)
  }
})
</script>

<style scoped>
/* ── 화면 레이아웃 ── */
.print-page {
  background: #f5f5f5;
  min-height: 100vh;
  padding: 20px;
}

.action-bar {
  text-align: center;
  margin-bottom: 20px;
}

.quotation-sheet {
  max-width: 780px;
  margin: 0 auto;
  background: #fff;
  padding: 30px 40px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  font-family: 'Malgun Gothic', '맑은 고딕', sans-serif;
  font-size: 12px;
  color: #222;
  line-height: 1.4;
  display: flex;
  flex-direction: column;
}

/* ── 헤더 ── */
.sheet-header {
  text-align: center;
  margin-bottom: 14px;
  position: relative;
}

.company-logo {
  position: absolute;
  left: 0;
  top: 0;
  font-size: 10px;
  color: #666;
  letter-spacing: 1px;
}

.logo-img {
  max-height: 28px;
  max-width: 120px;
}

.sheet-title {
  margin: 0;
  font-size: 24px;
  letter-spacing: 8px;
  font-weight: 700;
}

.sheet-date {
  margin-top: 4px;
  font-size: 12px;
  color: #555;
}

/* ── 기본정보 테이블 ── */
.info-tbl {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 10px;
  border-top: 2px solid #333;
  border-bottom: 2px solid #333;
}

.info-tbl th,
.info-tbl td {
  border: 1px solid #bbb;
  padding: 4px 8px;
  font-size: 12px;
}

.info-tbl th {
  background: #f2f2f2;
  font-weight: 600;
  width: 75px;
  text-align: center;
  white-space: nowrap;
}

.info-tbl td {
  width: calc(50% - 75px);
}

/* ── 섹션 타이틀 ── */
.section-title {
  font-size: 12px;
  font-weight: 700;
  margin: 8px 0 3px;
  padding: 3px 0;
  border-bottom: 1px solid #666;
  letter-spacing: 3px;
}

/* ── 자재 테이블 ── */
.mat-tbl {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 6px;
  font-size: 11px;
}

.mat-tbl th,
.mat-tbl td {
  border: 1px solid #bbb;
  padding: 3px 5px;
}

.mat-tbl thead th {
  background: #f2f2f2;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
}

.col-type { width: 36px; }
.col-name { width: 120px; }
.col-spec { width: auto; }
.col-price { width: 70px; }
.col-qty { width: 55px; }
.col-amt { width: 80px; }
.col-cmt { width: 36px; }

.type-cell {
  font-weight: 600;
  font-size: 11px;
  background: #fafafa;
  vertical-align: middle;
  writing-mode: vertical-rl;
  letter-spacing: 2px;
  padding: 4px 2px;
}

.subtotal-row {
  background: #f7f7f7;
}

.subtotal-row td {
  padding: 3px 5px;
  font-size: 11px;
}

/* ── 원가 요약 + 이미지 가로 배치 ── */
.cost-image-row {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin-bottom: 6px;
}

.cost-tbl {
  flex: 1;
  border-collapse: collapse;
}

.cost-tbl th,
.cost-tbl td {
  border: 1px solid #bbb;
  padding: 3px 8px;
  font-size: 12px;
}

.cost-tbl th {
  background: #f2f2f2;
  font-weight: 600;
  width: 100px;
  text-align: center;
  white-space: nowrap;
}

.design-image-area {
  flex-shrink: 0;
  width: 150px;
  text-align: center;
}

.image-label {
  font-size: 9px;
  font-weight: bold;
  color: #666;
  margin-bottom: 2px;
}

.design-img {
  max-width: 150px;
  max-height: 170px;
  border: 1px solid #ddd;
}

.highlight-row {
  background: #e8f0fe;
}

.total-row {
  background: #fff8e1;
}

.total-row th,
.total-row td {
  font-size: 12px;
  padding: 3px 6px;
}

.nego-row {
  background: #fef3e2;
}

.nego-row th,
.nego-row td {
  font-size: 12px;
  padding: 3px 6px;
  color: #e6a23c;
}

/* ── 사이즈 테이블 ── */
.size-tbl {
  width: auto;
  border-collapse: collapse;
  margin-bottom: 6px;
}

.size-tbl th,
.size-tbl td {
  border: 1px solid #bbb;
  padding: 3px 14px;
  font-size: 11px;
}

.size-tbl thead th {
  background: #f2f2f2;
  font-weight: 600;
  text-align: center;
}

/* ── 메모 ── */
.memo-box {
  margin-top: 4px;
  border: 1px solid #bbb;
}

.memo-label {
  background: #f2f2f2;
  padding: 2px 8px;
  font-weight: 600;
  font-size: 10px;
  border-bottom: 1px solid #bbb;
}

.memo-text {
  padding: 4px 8px;
  font-size: 10px;
  white-space: pre-wrap;
  min-height: 16px;
}

/* ── 하단 서명 ── */
.sheet-footer {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-top: auto;
  padding-top: 10px;
  border-top: 1px solid #ccc;
}

.footer-left {
  font-size: 10px;
  color: #666;
  line-height: 1.4;
}

.footer-left p {
  margin: 0;
}

.footer-company-name {
  font-size: 13px;
  font-weight: 700;
  color: #333;
  letter-spacing: 1px;
}

.footer-line {
  font-size: 11px;
}

.footer-right {
  text-align: center;
}

.author-info {
  display: block;
  font-size: 11px;
  color: #555;
  line-height: 1.5;
}

.seal-area {
  font-size: 13px;
  color: #999;
  margin-top: 6px;
}

/* ── 공통 ── */
.center { text-align: center; }
.right { text-align: right; }
.bold { font-weight: 700; }

/* ── 인쇄 스타일 ── */
@media print {
  .no-print {
    display: none !important;
  }

  .print-page {
    background: #fff;
    padding: 0;
    min-height: auto;
  }

  .quotation-sheet {
    box-shadow: none;
    padding: 0;
    max-width: 100%;
    min-height: calc(297mm - 16mm);
  }

  @page {
    size: A4;
    margin: 8mm;
  }

  /* 헤더/기본정보 — 함께 유지 */
  .sheet-header,
  .info-tbl {
    page-break-inside: avoid;
  }

  /* 자재 테이블 — 페이지 넘김 허용, thead 반복 */
  .mat-tbl thead {
    display: table-header-group;
  }
  .mat-tbl tr {
    page-break-inside: avoid;
  }

  /* 원가 요약 — 쪼개지지 않게 */
  .cost-image-row {
    page-break-inside: avoid;
  }

  /* 사이즈 배분 — 쪼개지지 않게 */
  .size-tbl {
    page-break-inside: avoid;
  }

  /* 메모 — 쪼개지지 않게 */
  .memo-box {
    page-break-inside: avoid;
  }

  /* 하단 서명 — 항상 함께 */
  .sheet-footer {
    page-break-inside: avoid;
  }

  /* 섹션 타이틀이 페이지 맨 아래 혼자 남지 않도록 */
  .section-title {
    page-break-after: avoid;
  }
}
</style>
